import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '@/contexts/LanguageContext';
import { fetchValueBets } from '@/api';
import SEOHead from '@/components/common/SEOHead';
import { LoadingPage } from '@/components/common/Loading';
import { formatOdds, formatPercent, formatDate, formatTime } from '@/lib/formatters';
import { MARKET_LABELS } from '@/lib/constants';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Target, ChevronRight, Clock } from 'lucide-react';

export default function ValueBetsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sport, setSport] = useState('');
  const [page, setPage] = useState(1);
  const { t } = useTranslation();
  const { currentLang } = useLanguage();

  useEffect(() => {
    setLoading(true);
    fetchValueBets({ sport: sport || undefined, page, limit: 20 })
      .then(res => { setData(res.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [sport, page]);

  return (
    <div className="container mx-auto px-4 max-w-7xl py-8" data-testid="value-bets-page">
      <SEOHead title={t('valueBets.title', 'Value Bets')} description={t('valueBets.subtitle', 'Selections priced above market average')} />

      <div className="flex items-center gap-2 text-sm text-slate-500 mb-2">
        <Link to={`/${currentLang}`} className="hover:text-slate-300">{t('nav.home')}</Link>
        <ChevronRight className="w-3 h-3" />
        <span className="text-slate-300">{t('valueBets.breadcrumb', 'Value Bets')}</span>
      </div>

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight uppercase text-slate-50 flex items-center gap-2" style={{ fontFamily: 'var(--font-heading)' }}>
            <Target className="w-8 h-8 md:w-10 md:h-10 text-amber-400" />
            {t('valueBets.title', 'Value Bets')}
          </h1>
          <p className="text-sm text-slate-500 mt-1">{t('valueBets.subtitle', 'Selections priced above market average')}</p>
        </div>
        <Select value={sport} onValueChange={v => { setSport(v === 'all' ? '' : v); setPage(1); }}>
          <SelectTrigger className="w-[140px] bg-slate-900 border-slate-700 text-sm" data-testid="sport-filter">
            <SelectValue placeholder={t('valueBets.allSports', 'All Sports')} />
          </SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-700">
            <SelectItem value="all">{t('valueBets.allSports', 'All Sports')}</SelectItem>
            <SelectItem value="football">{t('nav.football', 'Football')}</SelectItem>
            <SelectItem value="basketball">{t('nav.basketball', 'Basketball')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? <LoadingPage /> : (
        <>
          <div className="space-y-2" data-testid="value-bets-list">
            {data?.value_bets?.map(vb => (
              <Link key={vb.id} to={`/${currentLang}/match/${vb.match_slug}`}
                className="flex items-center justify-between border border-amber-500/20 bg-slate-900/50 hover:bg-slate-900 hover:border-amber-500/40 rounded-xl p-4 transition-all"
                data-testid={`vb-item-${vb.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/20 text-xs font-bold">
                      {formatPercent(vb.value_percent)} {t('valueBets.value', 'VALUE')}
                    </Badge>
                    {vb.league_logo && <img src={vb.league_logo} alt="" className="w-4 h-4 rounded object-contain" />}
                    <span className="text-xs text-slate-500">{vb.league_name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {vb.home_team_logo && <img src={vb.home_team_logo} alt="" className="w-5 h-5 object-contain" />}
                    <p className="text-sm font-medium text-slate-200">{vb.home_team || vb.match_name?.split(' vs ')[0]}</p>
                    <span className="text-xs text-slate-600">vs</span>
                    <p className="text-sm font-medium text-slate-200">{vb.away_team || vb.match_name?.split(' vs ')[1]}</p>
                    {vb.away_team_logo && <img src={vb.away_team_logo} alt="" className="w-5 h-5 object-contain" />}
                  </div>
                  <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                    <span>{MARKET_LABELS[vb.market_id] || vb.market_id} - {vb.selection}</span>
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {formatDate(vb.start_time)} {formatTime(vb.start_time)}</span>
                  </div>
                </div>
                <div className="text-right ml-4 shrink-0">
                  <p className="text-lg font-bold text-emerald-400 tabular-nums">{formatOdds(vb.best_odd)}</p>
                  <p className="text-xs text-slate-500">@ {vb.bookmaker_name}</p>
                  <p className="text-xs text-slate-600">avg: {formatOdds(vb.market_average)}</p>
                </div>
              </Link>
            ))}
            {!data?.value_bets?.length && (
              <div className="text-center py-12">
                <Target className="w-10 h-10 text-slate-700 mx-auto mb-3" />
                <p className="text-slate-400 mb-2">{t('valueBets.noResults', 'Şu an aktif değer bahisi bulunamadı')}</p>
                <p className="text-xs text-slate-600">Oranlar güncellendiğinde yeni fırsatlar otomatik olarak burada görünecektir.</p>
              </div>
            )}
          </div>

          {data?.pages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-6">
              {Array.from({ length: Math.min(data.pages, 5) }, (_, i) => i + 1).map(p => (
                <button key={p} onClick={() => setPage(p)}
                  className={`px-3 py-1.5 rounded text-sm ${page === p ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-800 text-slate-400 hover:bg-slate-700'}`}>
                  {p}
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
